import {isPlatformBrowser} from '@angular/common';
import {Component, ChangeDetectionStrategy, DestroyRef, PLATFORM_ID, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {interval, of} from 'rxjs';
import {catchError, filter} from 'rxjs/operators';
import {AuthService} from '../../services/auth.service';
import {PendingCountsService} from '../../services/pending-counts.service';
import {PaletteService} from '../../services/palette.service';
import {PendingCounts} from '../../interfaces/users/PendingCounts';
import {LIGHT_PALETTES, DARK_PALETTES} from '../../theme/palettes';
import {rNames} from '../../r.names';

const HIDDEN_KEY = 'adminBarHidden';

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Админская топ-панель: всегда в самом верху любой страницы для админа.
 * Слева — «новые события» (комментарии на модерации, заявки на оплату,
 * свежие регистрации), справа — селекторы палитр: превью применяется
 * только для админа (localStorage), «для всех» — в /admin/appearance.
 * Счётчики обновляются каждые 60с и при навигации.
 * ✕ прячет панель (localStorage), вернуть — кнопка «⚙ Админ» в левом нижнем углу.
 */
@Component({
    selector: 'app-admin-pending-bar',
    templateUrl: './admin-pending-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink]
})
export class AdminPendingBarComponent {
  private auth = inject(AuthService);
  private pendingService = inject(PendingCountsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected palette = inject(PaletteService);

  r = rNames;
  readonly lightPalettes = LIGHT_PALETTES;
  readonly darkPalettes = DARK_PALETTES;

  /** админ залогинен — панель видна всегда (SSR: false, появляется после гидратации) */
  isAdmin = signal(false);

  /** панель скрыта крестиком — помним в браузере админа */
  hidden = signal(false);

  /** null — счётчики не грузились / всё чисто */
  counts = signal<PendingCounts | null>(null);

  /** включено ли палитровое превью (показать кнопку сброса) */
  previewActive = computed(() =>
    this.palette.previewLight() !== null || this.palette.previewDark() !== null);

  constructor() {
    if (!this.isBrowser) return;
    this.hidden.set(readHidden());

    // перезапрос при навигации — ловит вход/выход админа без поллинга
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.refresh());

    interval(60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh());

    this.refresh();
  }

  setHidden(value: boolean): void {
    this.hidden.set(value);
    try {
      value ? localStorage.setItem(HIDDEN_KEY, '1') : localStorage.removeItem(HIDDEN_KEY);
    } catch { /* приватный режим — просто не запомним */ }
  }

  onPreview(mode: 'light' | 'dark', ev: Event): void {
    this.palette.preview(mode, (ev.target as HTMLSelectElement).value);
  }

  private refresh(): void {
    const admin = this.auth.isLoggedAdmin();
    this.isAdmin.set(admin);
    if (!admin) {
      this.counts.set(null);
      return;
    }
    this.pendingService.getPendingCounts()
      .pipe(catchError(() => of(null)))
      .subscribe(c => this.counts.set(c));
  }
}
